import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  PromptConfigCreateInputDto,
  PromptConfigOutputDto,
  PromptConfigSectionEvaluatorOutputDto,
  PromptConfigVersionCreateInputDto,
  PromptConfigVersionOutputDto,
  PromptContentDto,
} from '@kortxio/hub-api';
import { setError } from 'features/error/slice';
import api from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';
import { AsyncThunkConfig } from 'store/types';

export const getAllPromptConfigSectionEvaluators = createAsyncThunk<
  PromptConfigSectionEvaluatorOutputDto[],
  void,
  AsyncThunkConfig
>(
  'prompts/getAllPromptConfigSectionEvaluators',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.prompt.getAllPromptConfigSectionEvaluators();

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllPromptConfigs = createAsyncThunk<
  PromptConfigOutputDto[],
  void,
  AsyncThunkConfig
>('prompts/getAllPromptConfigs', async (_, { dispatch, rejectWithValue }) => {
  try {
    const response = await api.prompt.getAllPromptConfigs();

    return parseApiPayload(response);
  } catch (error) {
    const apiError = toApiError(error);

    if (!apiError.isAbortedError) {
      dispatch(
        setError({ message: apiError.message, uiMessage: apiError.uiMessage })
      );
    }

    return rejectWithValue(apiError);
  }
});

export const getPromptConfigById = createAsyncThunk<
  PromptConfigOutputDto,
  { promptConfigId: string },
  AsyncThunkConfig
>(
  'prompts/getPromptConfigById',
  async ({ promptConfigId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.prompt.getPromptConfigById(promptConfigId);

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getPromptConfigVersionById = createAsyncThunk<
  PromptConfigVersionOutputDto,
  { promptConfigId: string; versionId: string },
  AsyncThunkConfig
>(
  'prompts/getPromptConfigVersionById',
  async ({ promptConfigId, versionId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.prompt.getPromptConfigVersionById(
        promptConfigId,
        versionId
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const createPromptConfig = createAsyncThunk<
  PromptConfigOutputDto,
  { body: PromptConfigCreateInputDto },
  AsyncThunkConfig
>(
  'prompts/createPromptConfig',
  async ({ body }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.prompt.createPromptConfig(body);

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const createPromptConfigVersion = createAsyncThunk<
  PromptConfigOutputDto,
  { promptConfigId: string; body: PromptConfigVersionCreateInputDto },
  AsyncThunkConfig
>(
  'prompts/createPromptConfigVersion',
  async ({ promptConfigId, body }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.prompt.createPromptConfigVersion(
        promptConfigId,
        body
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const deletePromptConfigById = createAsyncThunk<
  void,
  { promptConfigId: string },
  AsyncThunkConfig
>(
  'prompts/deletePromptConfigById',
  async ({ promptConfigId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.prompt.deletePromptConfigById(promptConfigId);

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllCampaignReportPromptContents = createAsyncThunk<
  PromptContentDto[],
  { clientId: number; campaignId: number },
  AsyncThunkConfig
>(
  'prompts/getAllCampaignReportPromptContents',
  async ({ clientId, campaignId }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.promptContent.getAllCampaignReportPromptContents(
          clientId,
          campaignId
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);
